import { useCallback, useMemo } from "react";
import { usePermissions } from "./UsePermissions";
import type { Permission, User } from "../types/types";

type RequiredPermission = {
  path: string;
  method: string;
  scope: string;
  model: string;
};

type UsePermissionCheckerReturn = {
  hasPermission: (
    requiredPermissions: RequiredPermission[],
    requireAll?: boolean
  ) => boolean;
  getPermissionIdsByCriteria: (criteria: RequiredPermission) => string[];
  loading: boolean;
  userData: User | null;
  permissions: Permission[];
  error: string | null;
  isDataReady: boolean;
};

export const usePermissionChecker = (): UsePermissionCheckerReturn => {
  // Get data from the main permissions hook
  const { userData, permissions, loading, error, isDataReady } =
    usePermissions();

  const getPermissionIdsByCriteria = useCallback(
    (criteria: RequiredPermission): string[] => {
      if (!permissions.length) {
        return [];
      }

      return permissions
        .filter(
          (permission: Permission) =>
            permission.path === criteria.path &&
            permission.method === criteria.method &&
            permission.scope === criteria.scope &&
            permission.model === criteria.model
        )
        .map((permission: Permission) => permission.id);
    },
    [permissions]
  );

  const hasPermission = useCallback(
    (
      requiredPermissions: RequiredPermission[],
      requireAll: boolean = false
    ): boolean => {
      // Return false if data is still loading or not available
      if (!isDataReady) {
        return false;
      }

      if (!userData?.permissions?.length || !permissions.length) {
        return false;
      }

      // Check each required permission
      const permissionChecks = requiredPermissions.map((required) => {
        // Find permission IDs that match the required criteria
        const matchingPermissionIds = getPermissionIdsByCriteria(required);

        // Check if user has any of the matching permission IDs
        return matchingPermissionIds.some((permissionId: string) =>
          userData.permissions.includes(Number(permissionId))
        );
      });

      // If requireAll is true, user must have ALL permissions
      if (requireAll) {
        return permissionChecks.every((hasPermission) => hasPermission);
      }

      // If requireAll is false, user must have AT LEAST ONE permission
      return permissionChecks.some((hasPermission) => hasPermission);
    },
    [userData, permissions, getPermissionIdsByCriteria, isDataReady]
  );

  return {
    hasPermission,
    getPermissionIdsByCriteria,
    loading,
    userData,
    permissions,
    error,
    isDataReady,
  };
};
